import React, { useCallback, useEffect, useState } from 'react';

import { CompareContractsOutput } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import starTokens from '@amzn/meridian-tokens/base/icon/star';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import sparklesLargeIcon from 'src/icons/SparklesLarge';

import { SummaryBullets } from './summary-bullets';
import { SummaryFooter } from './summary-footer';

interface CompareBySummaryProps {
    summaryData: CompareContractsOutput | null;
    loading: boolean;
    error: string | null;
    onFallbackToTable: () => void;
    onRetry?: () => void;
    canRetry?: boolean;
    // New props for contract context
    leftId?: number;
    rightId?: number;
    leftTimestamp?: string;
    rightTimestamp?: string;
    contractIdMap?: Map<string, string>;
}

/**
 * Component for displaying LLM-generated contract comparison summaries.
 *
 * Provides loading states, error handling, and fallback options following
 * existing RECORD UI patterns and Meridian design system.
 */
export const CompareBySummary: React.FC<CompareBySummaryProps> = ({
    summaryData,
    loading,
    error,
    onFallbackToTable,
    onRetry,
    canRetry = false,
    leftId,
    rightId,
    leftTimestamp,
    rightTimestamp,
    contractIdMap
}) => {
    const [feedbackState, setFeedbackState] = useState<'neutral' | 'thumbs-up' | 'thumbs-down'>('neutral');

    // Reset feedback state when new summary is generated
    useEffect(() => {
        if (summaryData?.summary?.metadata?.generatedAt) {
            setFeedbackState('neutral');
        }
    }, [summaryData?.summary?.metadata?.generatedAt]);

    // Memoized feedback handler to prevent unnecessary re-renders
    const handleFeedbackChange = useCallback((state: 'neutral' | 'thumbs-up' | 'thumbs-down') => {
        setFeedbackState(state);
    }, []);

    // Empty state - no contracts selected
    if (!leftId || !rightId) {
        return null; // Blank component
    }

    if (loading) {
        return (
            <Column alignmentHorizontal="center" height="200px" spacing="m">
                <Loader type="circular" size="medium" />
                <Text>Generating business summary...</Text>
                <Text type="b300">This may take a few seconds</Text>
            </Column>
        );
    }

    if (error) {
        return (
            <Column alignmentHorizontal="center" height="200px" spacing="m">
                <Alert type="warning" size="medium">
                    {error}
                </Alert>
                <Row spacing="s">
                    {canRetry && onRetry && (
                        <Button onClick={onRetry} type="secondary">
                            Try Again
                        </Button>
                    )}
                    <Button onClick={onFallbackToTable} type="primary">
                        View Detailed Comparison
                    </Button>
                </Row>
            </Column>
        );
    }

    if (!summaryData?.summary?.summaryPoints?.length) {
        return (
            <Column spacing="m">
                <Alert type="neutral" size="medium">
                    No significant differences found between contracts.
                </Alert>
                <Text type="b300">The contracts appear to be identical or have only minor formatting differences.</Text>
            </Column>
        );
    }

    return (
        <Box type="outline" spacingInset="400">
            <Column spacing="l">
                <Row spacing="s" alignmentVertical="center">
                    <Button type="icon" size="small">
                        <Icon tokens={sparklesLargeIcon} />
                    </Button>
                    <Text type="h200">Contract Comparison Summary</Text>
                    <Tooltip
                        position="end"
                        title={`Generated using ${summaryData.summary.metadata?.modelUsed}${summaryData.summary.metadata?.processingTimeMs ? ` in ${summaryData.summary.metadata.processingTimeMs}ms` : ''}`}
                    >
                        <Button className="info-button" type="link" size="small">
                            <Icon className="info-button-icon" tokens={infoTokens} />
                        </Button>
                    </Tooltip>
                </Row>
                <Box type="fill" spacingInset="400" minHeight="200px">
                    <Column spacing="m">
                        <SummaryBullets points={summaryData.summary.summaryPoints} />
                    </Column>
                </Box>
                <SummaryFooter
                    feedbackState={feedbackState}
                    onFeedbackChange={handleFeedbackChange}
                    summaryData={summaryData}
                    contractContext={{
                        leftId,
                        rightId,
                        leftTimestamp,
                        rightTimestamp,
                        contractIdMap
                    }}
                />
            </Column>
        </Box>
    );
};
